/**
 * Range arithmetic by directional endpoint sweep.
 *
 * Not term-by-term interval arithmetic. Two reasons, both about credibility
 * rather than mathematics:
 *
 *   1. Interval arithmetic suffers the dependency problem — a variable
 *      appearing twice is treated as two independent variables, inflating the
 *      width. It also gets sign handling right only by accident when an input
 *      appears in a denominator or behind a subtraction.
 *
 *   2. A sweep is reproducible by hand. "Set every assumption to the value
 *      that makes this look worst, here is the number" is something a skeptic
 *      can check in a spreadsheet. A confidence interval is not.
 *
 * Deliberately NOT Monte Carlo. More defensible in the abstract, less
 * defensible in the room: a plain-language reader cannot audit a distribution,
 * and you cannot show the math.
 */

import { type Node, evalNode, refs, type NumScope } from './expr.js';
import { type Num, type Range, isRange, toRange } from './schema.js';

export type RangeScope = Record<string, Num>;

function at(v: Num, corner: 'lo' | 'mid' | 'hi'): number {
  return isRange(v) ? v[corner] : v;
}

function cornerScope(scope: RangeScope, corner: 'lo' | 'mid' | 'hi'): NumScope {
  const out: NumScope = {};
  for (const [k, v] of Object.entries(scope)) out[k] = at(v, corner);
  return out;
}

function midScope(scope: RangeScope): NumScope {
  return cornerScope(scope, 'mid');
}

/**
 * Evaluate an expression over ranged inputs.
 *
 * Cost is (n + 3) evaluations for n ranged inputs — microseconds. Each ranged
 * input is probed to find which direction pushes the result up, then the two
 * corners that minimise and maximise are evaluated directly.
 */
export function evaluateRange(node: Node, scope: RangeScope): Range {
  const mid = evalNode(node, midScope(scope));

  const ranged = refs(node).filter((id) => {
    const v = scope[id];
    return v !== undefined && isRange(v);
  });

  if (ranged.length === 0) return { lo: mid, mid, hi: mid };

  // Probe each ranged input independently: does pushing it to `hi` raise or
  // lower the result? Handles denominators and subtractions correctly.
  const direction: Record<string, 1 | -1> = {};
  for (const id of ranged) {
    const probe = midScope(scope);
    const v = scope[id]!;
    probe[id] = at(v, 'hi');
    const up = evalNode(node, probe);
    direction[id] = up >= mid ? 1 : -1;
  }

  const loScope = midScope(scope);
  const hiScope = midScope(scope);
  for (const id of ranged) {
    const v = scope[id]!;
    const dir = direction[id]!;
    // For the low corner, push each input toward whichever end lowers the result.
    loScope[id] = at(v, dir === 1 ? 'lo' : 'hi');
    hiScope[id] = at(v, dir === 1 ? 'hi' : 'lo');
  }

  let lo = evalNode(node, loScope);
  let hi = evalNode(node, hiScope);

  // The probe is local; for a non-monotonic expression the swept corners can
  // land the wrong way round. Order defensively rather than publish lo > hi.
  if (lo > hi) [lo, hi] = [hi, lo];
  // And the central estimate must sit inside its own band.
  return { lo: Math.min(lo, mid), mid, hi: Math.max(hi, mid) };
}

/** True when a range spans zero — the metric could be a cost or a benefit. */
export function crossesZero(r: Range): boolean {
  return r.lo < 0 && r.hi > 0;
}

/** True when lo, mid and hi are all the same — render as a plain number. */
export function isPoint(r: Range): boolean {
  return r.lo === r.mid && r.mid === r.hi;
}

export function scaleRange(r: Range, k: number): Range {
  const a = r.lo * k;
  const b = r.hi * k;
  return { lo: Math.min(a, b), mid: r.mid * k, hi: Math.max(a, b) };
}

export function addRanges(a: Range, b: Range): Range {
  return { lo: a.lo + b.lo, mid: a.mid + b.mid, hi: a.hi + b.hi };
}

/** Running total of a per-year series, preserving the band. */
export function cumulative(series: Range[]): Range[] {
  const out: Range[] = [];
  let acc: Range = { lo: 0, mid: 0, hi: 0 };
  for (const r of series) {
    acc = addRanges(acc, r);
    out.push({ ...acc });
  }
  return out;
}

/** Difference between two scenarios, band-aware. */
export function deltaRange(a: Range, b: Range): Range {
  const lo = a.lo - b.hi;
  const hi = a.hi - b.lo;
  return { lo: Math.min(lo, hi), mid: a.mid - b.mid, hi: Math.max(lo, hi) };
}

export function rangeOf(v: Num): Range {
  return toRange(v);
}
